import React, { useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { deleteUserQualification, fetchUserQualificationsByType } from "../../../services/api/user";
import AddLicenseAndCertificationModal from "../../CompleteYourProfile/components/AddLicenseAndCertificationModal";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";

const AddEditLicenseCertification = ({ userId, licencesId, certificationId }) => {
  const [isAddLicenseAndCertificationModalOpen, setIsAddLicenseAndCertificationModalOpen] =
    useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState(null);

  const [selectedUserCertificate, setSelectedUserCertificate] = useState({
    qualificationSubTypeId: null,
    startDate: null,
    endDate: null,
  });

  const isEdit = selectedUserCertificate?.qualificationSubTypeId ? true : false;
  const {
    isPending: isDeleteUserQualificationApiLoading,
    error: isDeleteUserQualificationApiError,
    data: deletedUserQualificationsData,
    mutateAsync: deleteUserQualificationMutation,
  } = useMutation({
    mutationFn: (id) => deleteUserQualification(id, userId),
    enabled: false,
  });

  const {
    data: certifications,
    isPending: isCertificationApiLoading,
    error: isCertificationApiError,
    refetch: refetchUserQualificationsByType,
  } = useQuery({
    queryKey: ["certifications", userId],

    queryFn: async () => {
      const [certificationSubs, licenseSubs] = await Promise.all([
        certificationId ? fetchUserQualificationsByType(userId, certificationId) : [],
        licencesId ? fetchUserQualificationsByType(userId, licencesId) : [],
      ]);
      return [...(certificationSubs?.data || []), ...(licenseSubs?.data || [])];
    },
    enabled: !!licencesId || !!certificationId,
  });

  const handleCertificateDelete = async () => {
    if (!certificateToDelete) return;
    try {
      await deleteUserQualificationMutation(certificateToDelete.id);
      await refetchUserQualificationsByType();
      setCertificateToDelete(null);
    } catch (error) {}
  };

  const handleEditCertificateButtonClicked = (certificate) => {
    try {
      setSelectedUserCertificate(certificate);
      setIsAddLicenseAndCertificationModalOpen(true);
    } catch (error) {}
  };

  return (
    <div className="mt-6 border-b pb-4">
      <div className="bg-light px-4 py-2 rounded-md flex justify-between items-center ">
        <h6 className="text-dark font-normal">License and Certifications</h6>
        <i
          className="fa-solid fa-circle-plus cursor-pointer text-lg text-secondary"
          onClick={() => setIsAddLicenseAndCertificationModalOpen(true)}
        ></i>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 my-4 px-4">
        {certifications?.map((certification) => (
          <div className="bg-light rounded-md py-3 px-4 flex justify-between border">
            <div>
              <p className="text-base font-medium">{certification?.qualificationSubType}</p>
              <div className="flex mt-2">
                <div className="flex ">
                  <p className="font-medium">Start Date: </p>
                  <p className="ml-2">{dayjs(certification?.startDate).format("DD/MM/YYYY")}</p>
                </div>
                <div className="flex  ml-9">
                  <p className="font-medium">End Date: </p>
                  <p className="ml-2">{dayjs(certification?.endDate).format("DD/MM/YYYY")}</p>
                </div>
              </div>
            </div>
            <div>
              <i
                className="fa-regular fa-trash-can text-red-600 cursor-pointer mr-3"
                onClick={() => setCertificateToDelete(certification)}
              ></i>
              <i
                className="fa-light fa-pencil text-gray-900 cursor-pointer"
                onClick={() => handleEditCertificateButtonClicked(certification)}
              ></i>
            </div>
          </div>
        ))}
      </div>

      {certificateToDelete && (
        <BaseConfirmModal
          open={!!certificateToDelete}
          onClose={() => setCertificateToDelete(null)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          type="warning"
          title="Delete License / Certification"
          description={`Are you sure you want to delete "${certificateToDelete?.qualificationSubType}"? This action cannot be undone.`}
          isLoading={isDeleteUserQualificationApiLoading}
          onConfirm={handleCertificateDelete}
        />
      )}

      {isAddLicenseAndCertificationModalOpen && (
        <AddLicenseAndCertificationModal
          open={isAddLicenseAndCertificationModalOpen}
          close={setIsAddLicenseAndCertificationModalOpen}
          refetchUserQualificationsByType={refetchUserQualificationsByType}
          licencesId={licencesId}
          certificationId={certificationId}
          isEdit={isEdit}
          setSelectedUserCertificate={setSelectedUserCertificate}
          selectedUserCertificate={selectedUserCertificate}
          qualificationSubTypeId={null}
          targetUserId={userId}
        />
      )}
    </div>
  );
};

export default AddEditLicenseCertification;
